import { getAnalytics } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";

import { FirebaseOptions } from "firebase/app";
import { fetchAndActivate, getRemoteConfig } from "firebase/remote-config";
import { JSX, useEffect, useMemo } from "react";
import {
  AuthProvider,
  FirebaseAppProvider,
  FirestoreProvider,
  RemoteConfigProvider,
  useFirebaseApp,
} from "reactfire";
import { SettingsProvider } from "../firestore/settings/SettingsProvider";
import { UserProvider } from "../firestore/user/UserProvider";
import { bootPhase } from "../utils/boot-perf";
import { isDevMode } from "../utils/debug";
import { initAuthWithoutPopupResolver } from "../utils/firebaseAuth";
import { createLogger } from "../utils/logger";

const logger = createLogger("FirebaseProvider");

const firebaseConfig: FirebaseOptions = {
  apiKey: "AIzaSyBbuXJGroutVSZmogMRdfMWgZVNa0UvF04",
  authDomain: "auth.iwanna.camp",
  projectId: "i-wanna-camp",
  storageBucket: "i-wanna-camp.firebasestorage.app",
  messagingSenderId: "268135617954",
  appId: "1:268135617954:web:6071ccd6ba41b4b4ae8efb",
  measurementId: "G-QEV2Y8SPF4",
};

const FirebaseAuthComponents = ({ children }: { children: JSX.Element }) => {
  const app = useFirebaseApp();
  // Not getAuth(): that eagerly loads the auth iframe from the custom
  // authDomain (~92KB, measured at 1,174ms on the critical path).
  const auth = initAuthWithoutPopupResolver(app);
  const firestore = getFirestore(app);

  // Firebase Analytics loads its own 150KB gtag. Nothing in the app reads the
  // reactfire AnalyticsProvider (no useAnalytics, no logEvent), so the only
  // thing getAnalytics() buys is the automatic page_view - which is no less
  // useful a moment later. Initialised on idle so it stays off the path to
  // first paint. The provider itself was removed as dead weight.
  useEffect(() => {
    const start = () => {
      try {
        getAnalytics(app);
      } catch {
        // Analytics is best-effort; a blocked or unsupported environment must
        // never affect rendering.
      }
    };
    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(start, { timeout: 5000 });
      return () => cancelIdleCallback(id);
    }
    const t = setTimeout(start, 2000);
    return () => clearTimeout(t);
  }, [app]);

  // Fixes a bug where the Firestore cache is not cleared when the user logs out
  // https://github.com/FirebaseExtended/reactfire/discussions/228
  useEffect(() => {
    const unsub = auth.beforeAuthStateChanged((user) => {
      if (!user) {
        const reactFirePreloadedObservables = (
          globalThis as Record<string, unknown>
        )["_reactFirePreloadedObservables"] as Map<string, unknown> | undefined;
        if (reactFirePreloadedObservables) {
          Array.from(reactFirePreloadedObservables.keys())
            .filter((key) => key.includes("firestore"))
            .forEach((key) => reactFirePreloadedObservables.delete(key));
        }
      }
    });
    return () => unsub();
  }, [auth]);

  // Remote Config used to gate the entire app behind a network round-trip:
  // this component returned null until fetchAndActivate() resolved, so nothing
  // painted until Firebase answered. getRemoteConfig() itself is synchronous,
  // so we build the instance immediately, render, and activate in background.
  const remoteConfigInstance = useMemo(() => {
    const remoteConfig = getRemoteConfig(app);

    // Consumed before the network fetch lands. These are kill-switches, so the
    // default is "on" - a fetched `false` turns the feature off a moment later.
    remoteConfig.defaultConfig = {
      loginEnabled: true,
      favoritesEnabled: true,
      leaveNoTraceEnabled: true,
      settingsSyncEnabled: true,
    };

    if (isDevMode()) {
      remoteConfig.settings = {
        minimumFetchIntervalMillis: 10000,
        fetchTimeoutMillis: 10000,
      };
    }
    return remoteConfig;
  }, [app]);

  // Fetch in the background purely to refresh the persisted values. We
  // deliberately do NOT re-render the tree when it lands.
  //
  // reactfire's remote-config observable emits exactly once, after
  // ensureInitialized(), and never re-emits. Forcing consumers to re-read
  // therefore means remounting them - and remounting this provider's subtree
  // tears down and rebuilds the MapLibre canvas mid-session, which both loses
  // map state and throws inside maplibre as queued callbacks touch a disposed
  // map. Not worth it for a feature flag.
  //
  // Consequence: values read this load come from the persisted cache (correct
  // for a returning visitor) or from defaultConfig above (a first visit). A
  // flipped flag therefore takes effect on the visitor's next load. That is the
  // normal Remote Config trade-off and is why the defaults are the "on" state.
  useEffect(() => {
    bootPhase("remote-config", () =>
      fetchAndActivate(remoteConfigInstance)
    ).catch((err) => {
      // A failed config fetch must not take the app down - defaults stand.
      logger.error("Remote config fetch failed", err);
    });
  }, [remoteConfigInstance]);

  if (!auth || !firestore) {
    return null;
  }

  return (
    <AuthProvider sdk={auth}>
      <FirestoreProvider sdk={firestore}>
        <RemoteConfigProvider sdk={remoteConfigInstance}>
          <UserProvider auth={auth} firestore={firestore}>
            <SettingsProvider auth={auth} firestore={firestore}>
              {children}
            </SettingsProvider>
          </UserProvider>
        </RemoteConfigProvider>
      </FirestoreProvider>
    </AuthProvider>
  );
};

const FirebaseProvider = ({ children }: { children: JSX.Element }) => {
  return (
    <FirebaseAppProvider firebaseConfig={firebaseConfig}>
      <FirebaseAuthComponents>{children}</FirebaseAuthComponents>
    </FirebaseAppProvider>
  );
};

export default FirebaseProvider;
